import { useState } from 'react';
import { WIDGET_COLORS } from '../../constants/colors';
import {
  formatPoints,
  formatCurrency,
  formatCurrencyShort,
  progressPercent,
  downPaymentPercent,
  formatContactMonth,
} from '../../utils/formatSavingsPoints';
import type { SavingsPointsData } from '../../hooks/useGetSavingsPoints';
import { WidgetCardHeader } from '../widgetCardHeader';
import { WidgetDivider } from '../widgetDivider';
import { ProgressItem } from '../progressItem';
import { StatRow } from '../statRow';
import { TipRow } from '../tipRow';
import { WidgetNotice } from '../widgetNotice';
import { InfoButton } from '../infoButton';
import { EligibilityInfoModal } from '../eligibilityInfoModal';

type Props = { data: SavingsPointsData };

export function BuildingPointsDownPaymentReady({ data }: Props) {
  const [infoOpen, setInfoOpen] = useState(false);

  const investmentNum = parseFloat(data.total_investment);
  const additionalNum = parseFloat(data.additional_downpayment_needed);
  const dpTarget = (investmentNum + additionalNum).toString();
  const pointsPercent = progressPercent(data.savings_points_earned, data.savings_points_required);
  const dpPercent = downPaymentPercent(data.total_investment, dpTarget);

  return (
    <>
      <WidgetCardHeader
        variant="default"
        badgeText="In Progress"
        description="Your savings are helping other families purchase their own homes while building towards purchasing your own."
      />

      <WidgetDivider />

      <ProgressItem
        label="Community Points"
        value={
          <span className="text-gray-500">
            <strong className="text-gray-800">{formatPoints(data.savings_points_earned)}</strong> of{' '}
            {formatPoints(data.savings_points_required)} required
          </span>
        }
        barPercent={pointsPercent}
        barColor={WIDGET_COLORS.dustBlue}
      />

      <ProgressItem
        label="Down Payment (GYS Home balance)"
        value={
          <span className="font-bold" style={{ color: WIDGET_COLORS.green.text }}>
            ✓ {formatCurrency(data.total_investment)} of {formatCurrency(dpTarget)}
          </span>
        }
        barPercent={dpPercent}
        barColor={WIDGET_COLORS.green.bar}
      />

      <StatRow
        left={{
          value: `${formatPoints(data.savings_points_earned)} pts`,
          valueColor: WIDGET_COLORS.primary,
          label: 'Community Points',
        }}
        right={{
          value: data.contact_month ? formatContactMonth(data.contact_month) : '-',
          valueColor: WIDGET_COLORS.primary,
          label: (
            <span className="inline-flex items-center uppercase">
              Estimated Eligibility
              <InfoButton onClick={() => setInfoOpen(true)} />
            </span>
          ),
        }}
      />

      <WidgetNotice variant="success" icon="check-circle">
        Your down payment is ready! Keep building your Community Points - you are on track. Once you
        have enough points, it will be your turn to apply for home finance.
      </WidgetNotice>

      {/* TODO: Re-enable once top-up suggestion logic is finalised
      {data.topup_suggestion_amount && data.topup_contact_month && data.topup_months_faster && (
        <TipRow
          left={{
            label: `If you top up to ${formatCurrencyShort(data.topup_suggestion_amount)}`,
            value: formatContactMonth(data.topup_contact_month),
            sub: `${data.topup_months_faster} months faster`,
          }}
          right={{
            label: 'Points shortfall',
            value: `${formatPoints((parseFloat(data.savings_points_required) - parseFloat(data.savings_points_earned)).toString())} pts`,
            sub: data.additional_months_at_current
              ? `~${parseFloat(data.additional_months_at_current).toFixed(1)} months at current rate`
              : undefined,
          }}
        />
      )}
      */}

      <EligibilityInfoModal open={infoOpen} onOpenChange={setInfoOpen} />
    </>
  );
}
